import { Controller } from "@hotwired/stimulus"
import { errorDetail, formatDate, getAllJSON, getJSON, sendJSON } from "../api.js"

// Konto: profil (PATCH me) + organizacje (read) + zespół/zaproszenia (create/accept).
// Zapraszać mogą owner/admin — 403 z API pokazujemy jako komunikat, nie ukrywamy formularza
// (uprawnienia egzekwuje serwer).
export default class extends Controller {
  static targets = ["nameInput", "profileSaved",
                    "orgRows", "orgStatus",
                    "inviteOrgSelect", "inviteEmail", "inviteRole", "inviteError",
                    "inviteRows", "inviteStatus", "acceptToken", "acceptMessage"]
  static values = {
    meUrl: String,            // /api/users/me/
    organizationsUrl: String, // /api/organizations/
    invitationsUrl: String,   // /api/invitations/
  }

  static INVITE_BADGE = {
    pending:  ["warm", "oczekuje"],
    accepted: ["sent", "przyjęte"],
    revoked:  ["new",  "odwołane"],
    expired:  ["hot",  "wygasłe"],
  }

  static ROLE_LABEL = { owner: "Właściciel", admin: "Administrator", member: "Członek" }

  async connect() {
    try {
      this.me = await getJSON(this.meUrlValue) // {name, url} — url = cel PATCH profilu
    } catch {
      this.me = null
    }
    await Promise.all([this.#loadOrgs(), this.loadInvitations()])
  }

  async saveProfile() {
    if (!this.me?.url) return
    const res = await sendJSON("PATCH", this.me.url, { name: this.nameInputTarget.value.trim() })
    if (this.hasProfileSavedTarget) this.profileSavedTarget.hidden = !res.ok
  }

  async invite() {
    const organization = this.inviteOrgSelectTarget.value
    const email = this.inviteEmailTarget.value.trim()
    if (!organization || !email) {
      this.#inviteError("Wybierz organizację i podaj e-mail.")
      return
    }
    const res = await sendJSON("POST", this.invitationsUrlValue, {
      organization: Number(organization),
      email,
      role: this.inviteRoleTarget.value,
    })
    if (!res.ok) {
      this.#inviteError(errorDetail(res.data, res.status === 403
        ? "Zapraszać mogą właściciel lub administrator organizacji."
        : "Nie udało się wysłać zaproszenia."))
      return
    }
    this.#inviteError(null)
    this.inviteEmailTarget.value = ""
    await this.loadInvitations()
  }

  async accept() {
    const token = this.acceptTokenTarget.value.trim()
    if (!token) return
    const res = await sendJSON("POST", `${this.invitationsUrlValue}accept/`, { token })
    if (this.hasAcceptMessageTarget) {
      this.acceptMessageTarget.hidden = false
      if (res.ok) {
        this.acceptMessageTarget.style.color = "var(--color-ls-green)"
        this.acceptMessageTarget.textContent = "Dołączono do organizacji ✓"
      } else {
        this.acceptMessageTarget.style.color = "var(--color-ls-red)"
        this.acceptMessageTarget.textContent = errorDetail(res.data, "Token niepoprawny lub wygasły.")
      }
    }
    if (res.ok) {
      this.acceptTokenTarget.value = ""
      await this.#loadOrgs()
    }
  }

  async loadInvitations() {
    let invitations
    try {
      invitations = await getAllJSON(this.invitationsUrlValue)
    } catch {
      this.#inviteStatus("Nie udało się załadować zaproszeń.")
      return
    }
    this.inviteRowsTarget.textContent = ""
    if (!invitations.length) {
      this.#inviteStatus("Brak zaproszeń (widoczne dla właściciela/administratora organizacji).")
      return
    }
    this.#inviteStatus(null)
    const template = document.getElementById("invite-row-template")
    for (const invitation of invitations) {
      const row = template.content.firstElementChild.cloneNode(true)
      row.querySelector('[data-field="email"]').textContent = invitation.email
      row.querySelector('[data-field="role"]').textContent =
        this.constructor.ROLE_LABEL[invitation.role] ?? invitation.role
      const [kind, label] = this.constructor.INVITE_BADGE[invitation.status] ?? ["new", invitation.status]
      const badge = row.querySelector('[data-field="badge"]')
      badge.className = `ls-badge ls-badge--${kind}`
      badge.textContent = label
      row.querySelector('[data-field="expires"]').textContent = formatDate(invitation.expires_at)
      this.inviteRowsTarget.appendChild(row)
    }
  }

  // --- prywatne ---

  async #loadOrgs() {
    let organizations
    try {
      organizations = await getAllJSON(this.organizationsUrlValue)
    } catch {
      this.#orgStatus("Nie udało się załadować organizacji.")
      return
    }
    this.orgRowsTarget.textContent = ""
    if (this.hasInviteOrgSelectTarget) {
      // odbuduj opcje (poza placeholderem)
      this.inviteOrgSelectTarget.querySelectorAll("option:not(:first-child)").forEach(o => o.remove())
    }
    if (!organizations.length) {
      this.#orgStatus("Nie należysz do żadnej organizacji.")
      return
    }
    this.#orgStatus(null)
    const template = document.getElementById("org-row-template")
    for (const org of organizations) {
      const row = template.content.firstElementChild.cloneNode(true)
      row.querySelector('[data-field="name"]').textContent = org.name
      const badge = row.querySelector('[data-field="badge"]')
      const active = org.status === "active"
      badge.className = `ls-badge ${active ? "ls-badge--sent" : "ls-badge--new"}`
      badge.textContent = org.status
      this.orgRowsTarget.appendChild(row)
      if (this.hasInviteOrgSelectTarget) {
        const option = document.createElement("option")
        option.value = org.id
        option.textContent = org.name
        this.inviteOrgSelectTarget.appendChild(option)
      }
    }
  }

  #orgStatus(message) {
    if (!this.hasOrgStatusTarget) return
    this.orgStatusTarget.hidden = message === null
    if (message !== null) this.orgStatusTarget.textContent = message
  }

  #inviteStatus(message) {
    if (!this.hasInviteStatusTarget) return
    this.inviteStatusTarget.hidden = message === null
    if (message !== null) this.inviteStatusTarget.textContent = message
  }

  #inviteError(message) {
    if (!this.hasInviteErrorTarget) return
    this.inviteErrorTarget.hidden = message === null
    if (message !== null) this.inviteErrorTarget.textContent = message
  }
}
